import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTechnologies,
  addTechnologie,
  updateTechnologie,
  deleteTechnologieById,
  setLoading,
  setError,
  setFormData,
  resetForm,
} from '../redux/technologie/actions';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const API_URL = `${BASE_URL}/api/technologie`;

const Technologie = () => {
  const dispatch = useDispatch();
  const { technologies, formData, loading, error } = useSelector((state) => state.technologie);

  const [tabStatus, setTabStatus] = useState('create');
  const [imageUploadState, setImageUploadState] = useState('');
  const [imageUploadError, setImageUploadError] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFormData({ ...formData, [name]: value }));
  };

  const handleImageChange = async (event) => {
    setImageUploadState('Uploading Image...');
    setImageUploadError('');
    const selectedFile = event.target.files[0];

    if (selectedFile && selectedFile.type.startsWith('image/')) {
      try {
        const res = await fetch(`${BASE_URL}/api/blog/getuploadurl`);
        const data = await res.json();

        if (data.url) {
          const uploadRes = await fetch(data.url, {
            method: 'PUT',
            body: selectedFile,
          });

          if (uploadRes.ok) {
            const imageUrl = data.url.split('?')[0];
            dispatch(setFormData({ ...formData, image: imageUrl }));
            setImageUploadState('Image Uploaded!');
            if (fileInputRef.current) fileInputRef.current.value = '';
          } else {
            throw new Error('Image upload failed');
          }
        }
      } catch (err) {
        setImageUploadError(err.message || 'Upload failed');
        setImageUploadState('');
      }
    } else {
      setImageUploadError('Please upload a valid image file.');
      setImageUploadState('');
      event.target.value = '';
    }
  };

  const fetchTechnologies = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      dispatch(setTechnologies(data.data));
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const saveTechnologie = async (e) => {
    e.preventDefault();
    if (!formData.name) return dispatch(setError('Name is required'));
    if (!formData.image && !formData._id) return dispatch(setError('Image is required'));

    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const isUpdate = !!formData._id;
      const url = isUpdate ? `${API_URL}/${formData._id}` : API_URL;
      const method = isUpdate ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, image: formData.image }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      dispatch(isUpdate ? updateTechnologie(data.data) : addTechnologie(data.data));
      dispatch(resetForm());
      setImageUploadState('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      alert(data.message);
      setTabStatus('browse');
      setSearchValue('');
    } catch (err) {
      dispatch(setError(err.message || 'Save failed'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const deleteTechnologie = async (id) => {
    if (!window.confirm('Are you sure you want to delete this technologie?')) return;
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const res = await fetch(`${API_URL}/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      dispatch(deleteTechnologieById(id));
      alert(data.message || 'Deleted successfully');
    } catch (err) {
      dispatch(setError(err.message || 'Delete failed'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const editTechnologie = (tech) => {
    dispatch(setFormData(tech));
    setTabStatus('create');
    setImageUploadState('');
    setImageUploadError('');
  };

  const handleSearch = () => {
    if (!searchValue.trim()) return technologies;
    return technologies.filter((tech) =>
      tech.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  useEffect(() => {
    fetchTechnologies();
  }, []);

  return (
    <div className="container py-4" style={{ minHeight: "100vh", padding: "32px" }}>
      <div>
        {/* Header & Tabs */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="fw-bold" style={{ color: "#1e3a8a", marginBottom: "0.5rem", letterSpacing: "-0.5px" }}>
              Technologies
            </h3>
            <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
              {technologies.length} {technologies.length === 1 ? 'technology' : 'technologies'}
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn"
              style={{
                padding: "10px 18px",
                borderRadius: "10px",
                background: tabStatus === 'create' ? "#e8efff" : "transparent",
                color: tabStatus === 'create' ? "#1e3a8a" : "#475569",
                border: "none",
                fontWeight: tabStatus === 'create' ? 600 : 500,
                fontSize: "15px",
                cursor: "pointer",
                transition: "all .25s ease",
              }}
              onClick={() => {
                setTabStatus('create');
                dispatch(resetForm());
                setImageUploadError('');
                setImageUploadState('');
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Create New
            </button>
            <button
              className="btn"
              style={{
                padding: "10px 18px",
                borderRadius: "10px",
                background: tabStatus === 'browse' ? "#e8efff" : "transparent",
                color: tabStatus === 'browse' ? "#1e3a8a" : "#475569",
                border: "none",
                fontWeight: tabStatus === 'browse' ? 600 : 500,
                fontSize: "15px",
                cursor: "pointer",
                transition: "all .25s ease",
              }}
              onClick={() => setTabStatus('browse')}
            >
              Browse
            </button>
          </div>
        </div>

        {/* Create Form */}
        {tabStatus === 'create' ? (
          <div className="card shadow-sm mb-4" style={{ borderRadius: "14px", maxWidth: "500px", margin: "0 auto" }}>
            <form onSubmit={saveTechnologie} className="p-4">
              <div className="mb-4">
                <label className="fw-semibold mb-2 d-block" style={{ color: "#333" }}>
                  Technologie Name
                </label>
                <input
                  type="text"
                  name="name"
                  className="form-control shadow-sm"
                  style={{
                    borderRadius: "6px",
                    borderColor: "#d0d5dd",
                    padding: "0.65rem 0.85rem",
                    fontSize: "16px",
                  }}
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="fw-semibold mb-2 d-block" style={{ color: "#333" }}>
                  Technologie Image
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="form-control shadow-sm"
                  style={{
                    borderRadius: "6px",
                    borderColor: "#d0d5dd",
                    padding: "0.65rem 0.85rem",
                    fontSize: "16px",
                  }}
                  onChange={handleImageChange}
                />
                {imageUploadState && <p className="text-primary small mt-2">{imageUploadState}</p>}
                {imageUploadError && <p className="text-danger small mt-2">{imageUploadError}</p>}
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Preview"
                    style={{ maxWidth: '100px', marginTop: "0.5rem", borderRadius: "6px", border: "1px solid #e5e7eb" }}
                  />
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100 fw-semibold"
                style={{
                  background: "linear-gradient(90deg, #1e3a8a, #2563eb)",
                  color: "white",
                  padding: "14px",
                  borderRadius: "8px",
                  fontSize: "17px",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(37, 99, 235, 0.12)",
                  cursor: "pointer",
                  transition: "0.25s ease",
                }}
                disabled={loading || imageUploadState === 'Uploading Image...'}
              >
                {loading ? (formData._id ? 'Updating...' : 'Creating...') : (formData._id ? 'Update' : 'Create')}
              </button>
              {error && <p className="text-danger mt-3">{error}</p>}
            </form>
          </div>
        ) : (
          // Browse Technologies
          <div className="card shadow-sm" style={{ borderRadius: "14px" }}>
            <div className="p-4">
              <input
                type="text"
                className="form-control shadow-sm mb-4"
                style={{
                  borderRadius: "6px",
                  borderColor: "#d0d5dd",
                  padding: "0.65rem 0.85rem",
                  fontSize: "16px",
                  maxWidth: "400px",
                }}
                placeholder="Search technologies..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <div className="row">
                {loading ? (
                  <div className="col-12 text-center py-5 text-muted">Loading...</div>
                ) : error ? (
                  <div className="col-12 text-center py-5 text-danger">{error}</div>
                ) : handleSearch().length === 0 ? (
                  <div className="col-12 text-center py-5 text-muted">
                    {searchValue ? 'No technologies found' : 'No technologies available. Please create some.'}
                  </div>
                ) : (
                  handleSearch().map((tech) => (
                    <div className="col-lg-3 col-md-4 col-sm-6 mb-4" key={tech._id}>
                      <div className="card h-100 shadow-sm" style={{ borderRadius: "10px", borderColor: "#d0d5dd" }}>
                        {tech.image ? (
                          <img
                            src={tech.image}
                            alt={tech.name}
                            className="card-img-top"
                            style={{
                              height: "140px",
                              objectFit: "cover",
                              borderTopLeftRadius: "10px",
                              borderTopRightRadius: "10px",
                              background: "#f3f4f6"
                            }}
                            onError={(e) => (e.target.src = '/fallback-image.jpg')}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center bg-light"
                            style={{
                              height: "140px",
                              borderTopLeftRadius: "10px",
                              borderTopRightRadius: "10px",
                              color: "#9ca3af",
                              fontSize: "1rem"
                            }}
                          >
                            No Image
                          </div>
                        )}
                        <div className="card-body d-flex flex-column">
                          <h5 className="card-title mb-2">{tech.name}</h5>
                          <hr />
                          <div className="d-flex justify-content-end gap-2 mt-auto">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => editTechnologie(tech)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => deleteTechnologie(tech._id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Technologie;